import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  Grid
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

function CompanyForm({ open, onClose, onSave, companyToEdit }) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    adres: '',
    woonplaats: '',
    contactpersoon: '',
    opmerkingen: '',
    sites: []
  });

  useEffect(() => {
    if (companyToEdit) {
      setFormData(companyToEdit);
    } else {
      setFormData({
        id: uuidv4(),
        name: '',
        adres: '',
        woonplaats: '',
        contactpersoon: '',
        opmerkingen: '',
        sites: []
      });
    }
  }, [companyToEdit]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSiteChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sites: prev.sites.map((site, i) => 
        i === index ? { ...site, [field]: value } : site
      )
    }));
  };

  const addSite = () => {
    setFormData(prev => ({
      ...prev,
      sites: [
        ...prev.sites,
        {
          id: uuidv4(),
          naamLocatie: '',
          eanCode: '',
          aansluitdienst: '',
          leveringGTV: 0,
          vermogenBeperktTot: 0,
          terugleveringGTV: 0,
          csvData: []
        }
      ]
    }));
  };

  const removeSite = (index) => {
    setFormData(prev => ({
      ...prev,
      sites: prev.sites.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {companyToEdit ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.adres}
                  onChange={(e) => handleChange('adres', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.woonplaats}
                  onChange={(e) => handleChange('woonplaats', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactpersoon}
                  onChange={(e) => handleChange('contactpersoon', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  value={formData.opmerkingen}
                  onChange={(e) => handleChange('opmerkingen', e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Sites</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addSite}
                  variant="outlined"
                  size="small"
                >
                  Add Site
                </Button>
              </Box>
              <Divider sx={{ my: 2 }} />
            </Box>

            {formData.sites.map((site, index) => (
              <Box key={site.id} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Site {index + 1}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeSite(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Location Name"
                      value={site.naamLocatie}
                      onChange={(e) => handleSiteChange(index, 'naamLocatie', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="EAN Code"
                      value={site.eanCode}
                      onChange={(e) => handleSiteChange(index, 'eanCode', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Connection Service"
                      value={site.aansluitdienst}
                      onChange={(e) => handleSiteChange(index, 'aansluitdienst', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Levering GTV (kW)"
                      type="number"
                      value={site.leveringGTV}
                      onChange={(e) => handleSiteChange(index, 'leveringGTV', Number(e.target.value))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vermogen Beperkt Tot (kW)"
                      type="number"
                      value={site.vermogenBeperktTot}
                      onChange={(e) => handleSiteChange(index, 'vermogenBeperktTot', Number(e.target.value))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Teruglevering GTV (kW)"
                      type="number"
                      value={site.terugleveringGTV}
                      onChange={(e) => handleSiteChange(index, 'terugleveringGTV', Number(e.target.value))}
                      required
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CompanyForm; 